import { useState } from "react";
import {
  useSchedulerStatus,
  useSchedulerEvents,
  useSchedulerHeartbeats,
  useKillScheduler,
  useResumeScheduler,
  useCancelEvent,
  type ScheduledEventItem,
  type HeartbeatItem,
} from "../api/scheduler";
import { formatRelative } from "../components/formatting";

function StatusBar() {
  const { data, isLoading, error } = useSchedulerStatus();
  const killMutation = useKillScheduler();
  const resumeMutation = useResumeScheduler();
  const [confirming, setConfirming] = useState(false);

  if (isLoading) return <div className="scheduler-status-bar">Loading...</div>;
  if (error || !data) return <div className="scheduler-status-bar error">Failed to load status</div>;

  const isKilled = data.killSwitch.active;

  function handleKillClick() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    killMutation.mutate();
    setConfirming(false);
  }

  function handleResumeClick() {
    resumeMutation.mutate();
  }

  return (
    <div className="scheduler-status-bar">
      <div className="scheduler-status-grid">
        <div className="scheduler-stat">
          <span className="scheduler-stat-label">Heartbeat</span>
          <span className={`scheduler-stat-value ${data.lastHeartbeat ? "alive" : "never"}`}>
            {data.lastHeartbeat
              ? `${data.lastHeartbeat.status} (${formatRelative(data.lastHeartbeat.ts)})`
              : "never"}
          </span>
        </div>
        <div className="scheduler-stat">
          <span className="scheduler-stat-label">Spend (24h)</span>
          <span className="scheduler-stat-value">
            ${data.spend24h.toFixed(2)} / ${data.spendLimit.toFixed(2)}
          </span>
        </div>
        <div className="scheduler-stat">
          <span className="scheduler-stat-label">Pending</span>
          <span className="scheduler-stat-value">{data.pendingCount} events</span>
        </div>
        <div className="scheduler-stat">
          <span className="scheduler-stat-label">Last Input</span>
          <span className="scheduler-stat-value">
            {data.lastUserInput
              ? `${formatRelative(data.lastUserInput.ts)} (${data.lastUserInput.channel})`
              : "unknown"}
          </span>
        </div>
      </div>
      <div className="scheduler-kill-section">
        <span className={`scheduler-kill-status ${isKilled ? "killed" : "active"}`}>
          {isKilled ? "KILLED" : "ACTIVE"}
        </span>
        {isKilled ? (
          <button className="btn scheduler-btn-resume" onClick={handleResumeClick} disabled={resumeMutation.isPending}>
            {resumeMutation.isPending ? "..." : "Resume"}
          </button>
        ) : (
          <button
            className={`btn scheduler-btn-kill ${confirming ? "confirming" : ""}`}
            onClick={handleKillClick}
            onBlur={() => setConfirming(false)}
            disabled={killMutation.isPending}
          >
            {killMutation.isPending ? "..." : confirming ? "Confirm Kill" : "Kill"}
          </button>
        )}
        {isKilled && data.killSwitch.reason && (
          <span className="scheduler-kill-reason">{data.killSwitch.activated_by}: {data.killSwitch.reason}</span>
        )}
      </div>
    </div>
  );
}

function EventsTable({ status }: { status: string }) {
  const { data, isLoading } = useSchedulerEvents(status);
  const cancelMutation = useCancelEvent();

  if (isLoading) return <div className="status-line">Loading events...</div>;
  if (!data || data.items.length === 0) return <div className="status-line">No {status} events.</div>;

  return (
    <table className="scheduler-table">
      <thead>
        <tr>
          <th>Type</th>
          <th>Title</th>
          <th>Scheduled</th>
          <th>Status</th>
          <th>Outcome</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((event: ScheduledEventItem) => (
          <tr key={event.id} className={`scheduler-event-row status-${event.status}`}>
            <td className="scheduler-type-badge">{event.type}</td>
            <td>{event.title}</td>
            <td>{formatRelative(event.scheduled_at)}</td>
            <td><span className={`scheduler-status-badge ${event.status}`}>{event.status}</span></td>
            <td className="scheduler-outcome">
              {event.outcome
                ? typeof event.outcome === "object" && "result" in event.outcome
                  ? String(event.outcome.result).slice(0, 60)
                  : typeof event.outcome === "object" && "error" in event.outcome
                    ? String(event.outcome.error).slice(0, 60)
                    : ""
                : ""}
            </td>
            <td>
              {event.status === "pending" && (
                <button
                  className="btn btn-sm scheduler-btn-cancel"
                  onClick={() => cancelMutation.mutate(event.id)}
                  disabled={cancelMutation.isPending}
                >
                  Cancel
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HeartbeatsTable() {
  const [limit, setLimit] = useState(20);
  const { data, isLoading } = useSchedulerHeartbeats(limit);

  if (isLoading) return <div className="status-line">Loading heartbeats...</div>;
  if (!data || data.items.length === 0) return <div className="status-line">No heartbeat logs yet.</div>;

  const hasMore = data.items.length >= limit;

  return (
    <>
    <table className="scheduler-table scheduler-heartbeat-table">
      <thead>
        <tr>
          <th>Time</th>
          <th>Status</th>
          <th>Due</th>
          <th>Fired</th>
          <th>Failed</th>
          <th>Duration</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((hb: HeartbeatItem) => {
          const ctx = hb.context as Record<string, unknown> | null;
          const ticks = ctx && typeof ctx.ticks === "number" ? ctx.ticks : null;
          return (
            <tr key={hb.id} className={`heartbeat-row status-${hb.status}`}>
              <td>{formatRelative(hb.ts)}</td>
              <td>
                <span className={`scheduler-status-badge ${hb.status}`}>{hb.status}</span>
                {ticks != null && ticks > 1 && <span className="scheduler-tick-count"> ({ticks} ticks)</span>}
              </td>
              <td>{hb.events_due}</td>
              <td>{hb.events_fired}</td>
              <td>{hb.events_failed}</td>
              <td>{hb.duration_ms != null ? `${hb.duration_ms}ms` : ""}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {hasMore && (
      <div className="load-more">
        <button className="btn" onClick={() => setLimit((l) => l + 20)}>Load older</button>
      </div>
    )}
    </>
  );
}

export function SchedulerView() {
  return (
    <div className="scheduler-view">
      <StatusBar />

      <section className="scheduler-section">
        <h2>Pending Events</h2>
        <EventsTable status="pending" />
      </section>

      <section className="scheduler-section">
        <h2>Recent History</h2>
        <EventsTable status="all" />
      </section>

      <section className="scheduler-section">
        <h2>Heartbeat Log</h2>
        <HeartbeatsTable />
      </section>
    </div>
  );
}
